import { useEffect, useState } from 'react';
import { adminAPI, fmtError } from '@/lib/api';
import { formatInt, prettyPhase, prettyPlan } from '@/lib/format';

/**
 * Generic Phase requests page — filtered by target phase.
 * - target='phase_2' → Phase 2 requests (Core/Global phase_1 → phase_2)
 * - target='funded'  → Funded requests (Core/Global phase_2 → funded, Split phase_1 → funded)
 */
export default function AdminPhaseRequests({ target, title, subtitle }) {
  const [list, setList] = useState([]);
  const [modal, setModal] = useState(null); // {mode: 'approve'|'deny', req}
  const [creds, setCreds] = useState({ mt_login: '', mt_password: '', mt_investor_password: '', mt_broker: 'Deriv', mt_server: 'Deriv-Demo', note: '' });
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => adminAPI.phaseRequests('pending', target).then(setList);
  useEffect(() => { load(); }, [target]);

  const openApprove = (r) => {
    setCreds({ mt_login: '', mt_password: '', mt_investor_password: '', mt_broker: 'Deriv', mt_server: 'Deriv-Demo', note: '' });
    setErr(''); setModal({ mode: 'approve', req: r });
  };
  const openDeny = (r) => { setReason(''); setErr(''); setModal({ mode: 'deny', req: r }); };
  const close = () => { setModal(null); setErr(''); setReason(''); };

  const reset = async (r) => {
    try { await adminAPI.resetPhase(r.request_id); load(); }
    catch (e) { alert(fmtError(e)); }
  };

  const submitApprove = async () => {
    setErr(''); setSaving(true);
    try {
      await adminAPI.approvePhase(modal.req.request_id, creds);
      close(); load();
    } catch (e) { setErr(fmtError(e)); }
    finally { setSaving(false); }
  };
  const submitDeny = async () => {
    setErr(''); setSaving(true);
    try { await adminAPI.denyPhase(modal.req.request_id, reason); close(); load(); }
    catch (e) { setErr(fmtError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="label-sm mb-2">Admin</div>
        <h1 className="font-display text-3xl">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      {list.length === 0 && <div className="card-tech rounded-lg p-10 text-center text-zinc-500 text-sm">No pending requests</div>}
      {list.map(r => (
        <div key={r.request_id} className="card-tech rounded-lg p-5 flex flex-wrap items-start justify-between gap-3" data-testid={`phase-req-${r.request_id}`}>
          <div>
            <div className="label-xs">REQUEST #{r.request_id} · TARGET {(r.target_phase || '').toUpperCase().replace('_',' ')}</div>
            <div className="font-display text-lg mt-1">{r.user_name} <span className="text-zinc-500 text-sm">· {r.user_email}</span></div>
            <div className="text-xs text-zinc-500 mt-0.5">
              #{r.account?.mt_login} · {prettyPlan(r.account?.plan_id)} · ${formatInt(r.account?.account_size)} · currently on {prettyPhase(r.from_phase)}
            </div>
            {r.note && <div className="mt-2 text-sm text-zinc-400">Trader note: {r.note}</div>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button className="btn-primary btn-sm" onClick={() => openApprove(r)} data-testid={`approve-${r.request_id}`}>Approve</button>
            <button className="btn-ghost btn-sm text-[#ff3366]" onClick={() => openDeny(r)} data-testid={`deny-${r.request_id}`}>Decline</button>
            <button className="btn-ghost btn-sm" onClick={() => reset(r)} data-testid={`reset-${r.request_id}`}>Reset</button>
          </div>
        </div>
      ))}

      {modal?.mode === 'approve' && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in" onClick={close}>
          <div className="card-tech rounded-lg p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="label-sm">Approve & issue credentials</div>
            <h3 className="font-display text-2xl mt-1">
              {modal.req.target_phase === 'phase_2' ? 'Phase 2 credentials' : 'Funded phase credentials'}
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              Trader: {modal.req.user_name} · #{modal.req.account?.mt_login}. Fill in the NEW MT5 credentials — these will be emailed to the trader and their account will be rotated.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="label-xs mb-1">Broker</div>
                <input className="input-tech" value={creds.mt_broker} onChange={e => setCreds({ ...creds, mt_broker: e.target.value })} data-testid="creds-broker" />
              </div>
              <div>
                <div className="label-xs mb-1">Server</div>
                <input className="input-tech" value={creds.mt_server} onChange={e => setCreds({ ...creds, mt_server: e.target.value })} data-testid="creds-server" />
              </div>
              <div>
                <div className="label-xs mb-1">Login ID</div>
                <input className="input-tech font-mono" value={creds.mt_login} onChange={e => setCreds({ ...creds, mt_login: e.target.value })} data-testid="creds-login" />
              </div>
              <div>
                <div className="label-xs mb-1">Password</div>
                <input className="input-tech font-mono" value={creds.mt_password} onChange={e => setCreds({ ...creds, mt_password: e.target.value })} data-testid="creds-password" />
              </div>
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Investor password</div>
                <input className="input-tech font-mono" value={creds.mt_investor_password} onChange={e => setCreds({ ...creds, mt_investor_password: e.target.value })} data-testid="creds-investor" />
              </div>
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Note to trader (optional)</div>
                <textarea className="input-tech" rows={2} value={creds.note} onChange={e => setCreds({ ...creds, note: e.target.value })} data-testid="creds-note" />
              </div>
            </div>
            {err && <div className="text-sm text-[#ff3366] mt-3">{err}</div>}
            <div className="flex gap-2 mt-4">
              <button disabled={saving || !creds.mt_login || !creds.mt_password || !creds.mt_broker || !creds.mt_server} className="btn-primary btn-sm" onClick={submitApprove} data-testid="submit-approve-creds">
                {saving ? 'Approving…' : 'Approve & email credentials'}
              </button>
              <button className="btn-ghost btn-sm" onClick={close}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {modal?.mode === 'deny' && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="card-tech rounded-lg p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-2xl">Decline request</h3>
            <textarea className="input-tech mt-4" rows={3} placeholder="Reason (visible to trader)" value={reason} onChange={e => setReason(e.target.value)} data-testid="deny-reason" />
            {err && <div className="text-sm text-[#ff3366] mt-2">{err}</div>}
            <div className="flex gap-2 mt-3">
              <button disabled={!reason || saving} className="btn-primary" onClick={submitDeny} data-testid="submit-deny">Confirm decline</button>
              <button className="btn-ghost" onClick={close}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
